import { IconFilter, IconSelector, IconSortAscending, IconSortDescending, IconX } from '@tabler/icons-react'
import cx from 'clsx'
import { endOfDay, endOfMonth, startOfDay, startOfMonth, subDays, subMonths } from 'date-fns'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import DatePicker from '@components/TypeableDatePicker'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'

import Checkbox from '@components/Checkbox'

import { SortOrder } from '@type/common'

import styles from './index.module.css'
import { computeAsyncDisplayOptions } from './asyncDisplayOptions'

export interface FilterOption {
  value: string
  label: string
  // Soft-deleted / archived entries (removed workflows, deleted pipelines)
  // are still listed so their past requests stay filterable, but rendered
  // in a muted color to signal they're inactive.
  archived?: boolean
}

export interface DateFilterValue {
  startDate?: Date
  endDate?: Date
  preset?: string
}

export interface FilterableColumnHeaderProps {
  children: React.ReactNode
  order?: SortOrder
  onSortClick?: React.MouseEventHandler<Element>
  filterOptions?: FilterOption[]
  selectedFilters?: string[]
  onFilterChange?: (values: string[]) => void
  className?: string
  isDateColumn?: boolean
  dateValue?: DateFilterValue
  onDateChange?: (value: DateFilterValue | null) => void
  isTextSearch?: boolean
  textValue?: string
  onTextChange?: (value: string) => void
  asyncSearchFn?: (query: string) => Promise<FilterOption[]>
  onAsyncFilterChange?: (options: FilterOption[]) => void
  isNumericFilter?: boolean
}

const DATE_PRESETS = [
  {
    id: 'today',
    labelKey: 'datePresets.today' as const,
    getDates: () => ({ startDate: startOfDay(new Date()), endDate: endOfDay(new Date()) }),
  },
  {
    id: 'yesterday',
    labelKey: 'datePresets.yesterday' as const,
    getDates: () => ({ startDate: startOfDay(subDays(new Date(), 1)), endDate: endOfDay(subDays(new Date(), 1)) }),
  },
  {
    id: 'last7days',
    labelKey: 'datePresets.last7days' as const,
    getDates: () => ({ startDate: startOfDay(subDays(new Date(), 7)), endDate: endOfDay(new Date()) }),
  },
  {
    id: 'last30days',
    labelKey: 'datePresets.last30days' as const,
    getDates: () => ({ startDate: startOfDay(subDays(new Date(), 30)), endDate: endOfDay(new Date()) }),
  },
  {
    id: 'thisMonth',
    labelKey: 'datePresets.thisMonth' as const,
    getDates: () => ({ startDate: startOfMonth(new Date()), endDate: endOfMonth(new Date()) }),
  },
  {
    id: 'lastMonth',
    labelKey: 'datePresets.lastMonth' as const,
    getDates: () => ({
      startDate: startOfMonth(subMonths(new Date(), 1)),
      endDate: endOfMonth(subMonths(new Date(), 1)),
    }),
  },
]

export const FilterableColumnHeader: React.FC<FilterableColumnHeaderProps> = ({
  children,
  order,
  onSortClick,
  filterOptions = [],
  selectedFilters = [],
  onFilterChange,
  className,
  isDateColumn = false,
  dateValue,
  onDateChange,
  isTextSearch = false,
  textValue = '',
  onTextChange,
  asyncSearchFn,
  onAsyncFilterChange,
  isNumericFilter = false,
}) => {
  const { t } = useTranslation('common')
  const [isOpen, setIsOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [showCustomDatePicker, setShowCustomDatePicker] = useState(false)
  const [customDateRange, setCustomDateRange] = useState<[Date | null, Date | null]>([null, null])
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, maxHeight: 400 })
  const [asyncOptions, setAsyncOptions] = useState<FilterOption[]>([])
  const [isLoadingAsync, setIsLoadingAsync] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const filterButtonRef = useRef<HTMLButtonElement>(null)

  const hasActiveFilters =
    selectedFilters.length > 0 ||
    // ``onDateChange(null)`` clears the date filter; the start of the
    // range is also ``null`` while the user is in the middle of
    // picking a date range. Reject both null AND undefined as
    // "filter inactive" — checking ``!== undefined`` alone would
    // light up the filter chip on a cleared selection.
    (dateValue?.startDate !== undefined && dateValue.startDate !== null) ||
    ((isTextSearch || isNumericFilter) && !!textValue)

  // Calculate dropdown position when opening, accounting for viewport bounds
  const updateDropdownPosition = useCallback(() => {
    if (filterButtonRef.current) {
      const rect = filterButtonRef.current.getBoundingClientRect()
      const viewportHeight = window.innerHeight
      const viewportWidth = window.innerWidth
      const spaceBelow = viewportHeight - rect.bottom - 8 // 8px margin from bottom
      const spaceAbove = rect.top - 8 // 8px margin from top
      const estimatedDropdownHeight = isDateColumn ? 350 : 280 // Approximate height
      const estimatedDropdownWidth = isDateColumn ? 300 : 220 // Approximate width

      let top: number
      let maxHeight: number
      let left: number = rect.left

      if (spaceBelow >= estimatedDropdownHeight) {
        // Enough space below - position normally
        top = rect.bottom + 4
        maxHeight = Math.min(spaceBelow, 400)
      } else if (spaceAbove > spaceBelow) {
        // More space above - position above the button
        maxHeight = Math.min(spaceAbove, 400)
        top = rect.top - Math.min(estimatedDropdownHeight, maxHeight) - 4
      } else {
        // Default to below but constrain height
        top = rect.bottom + 4
        maxHeight = Math.max(spaceBelow, 150) // Minimum 150px height
      }

      // Check horizontal overflow - if dropdown would extend past viewport, align to right edge
      if (left + estimatedDropdownWidth > viewportWidth - 8) {
        // Align dropdown's right edge with button's right edge, or viewport edge with margin
        left = Math.max(8, viewportWidth - estimatedDropdownWidth - 8)
      }

      setDropdownPosition({
        top,
        left,
        maxHeight,
      })
    }
  }, [isDateColumn])

  const filteredOptions = useMemo(() => {
    if (!searchTerm) return filterOptions
    return filterOptions.filter((opt) => opt.label.toLowerCase().includes(searchTerm.toLowerCase()))
  }, [filterOptions, searchTerm])

  // Debounced async search
  useEffect(() => {
    if (!asyncSearchFn) return
    if (!isOpen || !searchTerm) {
      setAsyncOptions([])
      setIsLoadingAsync(false)
      return
    }
    let ignored = false
    setIsLoadingAsync(true)
    const timer = setTimeout(async () => {
      try {
        const results = await asyncSearchFn(searchTerm)
        if (!ignored) setAsyncOptions(results)
      } catch {
        if (!ignored) setAsyncOptions([])
      } finally {
        if (!ignored) setIsLoadingAsync(false)
      }
    }, 300)
    return () => {
      ignored = true
      clearTimeout(timer)
    }
  }, [searchTerm, asyncSearchFn, isOpen])

  const asyncDisplayOptions = useMemo(
    () => computeAsyncDisplayOptions({ filterOptions, asyncOptions, selectedFilters, searchTerm }),
    [filterOptions, asyncOptions, selectedFilters, searchTerm],
  )

  const handleFilterToggle = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation()
      updateDropdownPosition()
      setIsOpen((prev) => !prev)
      setSearchTerm('')
      setShowCustomDatePicker(false)
    },
    [updateDropdownPosition],
  )

  const handleClose = useCallback(() => {
    setIsOpen(false)
    setSearchTerm('')
    setShowCustomDatePicker(false)
  }, [])

  // Use a ref to always have the latest selectedFilters to avoid stale closure issues
  const selectedFiltersRef = useRef(selectedFilters)
  selectedFiltersRef.current = selectedFilters

  const asyncDisplayOptionsRef = useRef(asyncDisplayOptions)
  asyncDisplayOptionsRef.current = asyncDisplayOptions

  const handleFilterOptionToggle = useCallback(
    (value: string) => {
      if (!onFilterChange) return
      const currentFilters = selectedFiltersRef.current
      const newFilters = currentFilters.includes(value)
        ? currentFilters.filter((f) => f !== value)
        : [...currentFilters, value]
      onFilterChange(newFilters)
      if (asyncSearchFn && onAsyncFilterChange) {
        const allOptions = asyncDisplayOptionsRef.current
        const newOptions = newFilters
          .map((id) => allOptions.find((o) => o.value === id))
          .filter((o): o is FilterOption => o !== undefined)
        onAsyncFilterChange(newOptions)
      }
    },
    [onFilterChange, asyncSearchFn, onAsyncFilterChange],
  )

  const handleClearFilters = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation()
      if (isDateColumn && onDateChange) {
        onDateChange(null)
      } else if ((isTextSearch || isNumericFilter) && onTextChange) {
        onTextChange('')
      } else if (asyncSearchFn && onAsyncFilterChange) {
        onFilterChange?.([])
        onAsyncFilterChange([])
      } else if (onFilterChange) {
        onFilterChange([])
      }
      setShowCustomDatePicker(false)
    },
    [isDateColumn, isTextSearch, asyncSearchFn, onDateChange, onTextChange, onFilterChange, onAsyncFilterChange],
  )

  const handleDatePresetClick = useCallback(
    (preset: (typeof DATE_PRESETS)[0]) => {
      if (!onDateChange) return
      const dates = preset.getDates()
      onDateChange({ ...dates, preset: preset.id })
    },
    [onDateChange],
  )

  const handleCustomDateChange = useCallback(
    (dates: [Date | null, Date | null]) => {
      setCustomDateRange(dates)
      const [start, end] = dates
      if (start && end && onDateChange) {
        // Apply startOfDay to start date and endOfDay to end date
        // This ensures the full day range is included when start and end are the same day
        onDateChange({ startDate: startOfDay(start), endDate: endOfDay(end), preset: 'custom' })
      }
    },
    [onDateChange],
  )

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        filterButtonRef.current &&
        !filterButtonRef.current.contains(event.target as Node)
      ) {
        handleClose()
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen, handleClose])

  const SortIcon = useMemo(() => {
    if (order === SortOrder.asc) return IconSortAscending
    if (order === SortOrder.desc) return IconSortDescending
    return IconSelector
  }, [order])

  return (
    <div className={styles.headerWrapper}>
      {/* Main header button - clicking cycles sort (same as ID field) */}
      {onSortClick ? (
        <button type="button" className={cx(styles.headerButton, className)} onClick={onSortClick}>
          {children}
          <SortIcon
            className={cx(
              'h-4 w-4 box-content hover:outline hover:outline-blue-50 hover:bg-blue-50 rounded-md',
              order ? 'text-brand_primary' : 'text-slate-950',
            )}
          />
        </button>
      ) : (
        <span className={cx(styles.headerButton, className)}>{children}</span>
      )}

      {/* Filter dropdown button */}
      <button
        ref={filterButtonRef}
        type="button"
        className={cx(styles.filterButton, hasActiveFilters && styles.filterButtonActive)}
        onClick={handleFilterToggle}
      >
        <IconFilter className="h-3 w-3" />
      </button>

      {isOpen &&
        createPortal(
          <div
            ref={dropdownRef}
            className={styles.dropdown}
            style={{
              position: 'fixed',
              top: dropdownPosition.top,
              left: dropdownPosition.left,
              maxHeight: dropdownPosition.maxHeight,
              overflowY: 'auto',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Filter Section */}
            <div className={styles.filterSection}>
              {asyncSearchFn ? (
                <>
                  <input
                    type="text"
                    className={styles.searchInput}
                    placeholder={t('filter.search')}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                    autoFocus
                  />
                  <div className={styles.optionsList}>
                    {isLoadingAsync && <div className="px-2 py-1.5 text-sm text-gray-400">{t('filter.loading')}</div>}
                    {!isLoadingAsync &&
                      asyncDisplayOptions.map((option) => (
                        <label key={option.value} className={styles.optionItem} onClick={(e) => e.stopPropagation()}>
                          <Checkbox
                            className={styles.optionCheckbox}
                            checked={selectedFilters.includes(option.value)}
                            onChange={() => handleFilterOptionToggle(option.value)}
                          />
                          <span className={option.archived ? 'text-gray-400' : undefined}>
                            {option.label}
                            {option.archived && (
                              <span className="ml-1 text-xs text-gray-400">{t('filter.archivedSuffix')}</span>
                            )}
                          </span>
                        </label>
                      ))}
                    {!isLoadingAsync && asyncDisplayOptions.length === 0 && (
                      // Unified empty-state: same wording as the static-facet
                      // branch below. The previous "Type to search..." hint
                      // for no-query async columns was confusing when the
                      // pre-fetched list was empty due to scope narrowing —
                      // QA read it as "the dropdown is broken, just sits
                      // there". Saying "No options found" sets the right
                      // expectation; the search input itself signals you
                      // can still type.
                      <div className="px-2 py-1.5 text-sm text-gray-500">{t('filter.noOptionsFound')}</div>
                    )}
                  </div>
                </>
              ) : isNumericFilter ? (
                <div className="px-2 py-1.5">
                  <label className="block text-xs text-gray-500 mb-1">{t('filter.minDays')}</label>
                  <input
                    type="number"
                    min="0"
                    step="0.5"
                    className={styles.searchInput}
                    placeholder="0"
                    value={textValue}
                    onChange={(e) => onTextChange?.(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                    autoFocus
                  />
                </div>
              ) : isTextSearch ? (
                <input
                  type="text"
                  className={styles.searchInput}
                  placeholder={t('filter.search')}
                  value={textValue}
                  onChange={(e) => onTextChange?.(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                  autoFocus
                />
              ) : isDateColumn ? (
                <div className={styles.datePresets}>
                  {DATE_PRESETS.map((preset) => (
                    <button
                      key={preset.id}
                      type="button"
                      className={cx(
                        styles.datePresetButton,
                        dateValue?.preset === preset.id && styles.datePresetActive,
                      )}
                      onClick={() => handleDatePresetClick(preset)}
                    >
                      {t(preset.labelKey)}
                    </button>
                  ))}
                  <button
                    type="button"
                    className={cx(styles.datePresetButton, dateValue?.preset === 'custom' && styles.datePresetActive)}
                    onClick={() => setShowCustomDatePicker(true)}
                  >
                    {t('datePresets.customRange')}
                  </button>
                  {showCustomDatePicker && (
                    <div className="mt-2">
                      <DatePicker
                        selected={customDateRange[0]}
                        onChange={handleCustomDateChange}
                        startDate={customDateRange[0]}
                        endDate={customDateRange[1]}
                        selectsRange
                        inline
                      />
                    </div>
                  )}
                </div>
              ) : (
                // Regular filter UI
                <>
                  <input
                    type="text"
                    className={styles.searchInput}
                    placeholder={t('filter.search')}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                  />
                  <div className={styles.optionsList}>
                    {filteredOptions.map((option) => (
                      <label key={option.value} className={styles.optionItem} onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                          className={styles.optionCheckbox}
                          checked={selectedFilters.includes(option.value)}
                          onChange={() => handleFilterOptionToggle(option.value)}
                        />
                        <span className={option.archived ? 'text-gray-400' : undefined}>{option.label}</span>
                      </label>
                    ))}
                    {filteredOptions.length === 0 && (
                      <div className="px-2 py-1.5 text-sm text-gray-500">{t('filter.noOptionsFound')}</div>
                    )}
                  </div>
                </>
              )}

              {hasActiveFilters && (
                <button type="button" className={styles.clearButton} onClick={handleClearFilters}>
                  <IconX className="inline h-3 w-3 mr-1" />
                  {t('filter.clearFilters')}
                </button>
              )}
            </div>
          </div>,
          document.body,
        )}
    </div>
  )
}

export default FilterableColumnHeader
